import React from 'react';
import type { Rack, Recipe } from '@recipe-rack/shared';

import { RackChip } from '../../design/components';

export interface RecipeCardProps {
  recipe: Recipe;
  racks: Rack[];
  isOpen?: boolean;
  onClick?: (recipeId: string) => void;
}

/** Extract a friendly source domain from a URL string. */
function sourceDomain(link: string | undefined): string {
  if (!link) return '';
  try {
    const u = new URL(link);
    return u.hostname.replace(/^www\./, '');
  } catch {
    return link;
  }
}

/**
 * One recipe card in a row or grid.
 *  - Pending recipes (`version === 0`) render a shimmer + italic placeholder.
 *  - Otherwise: optional thumbnail (gradient fallback) + title + meta + rack pills.
 */
const RecipeCard: React.FC<RecipeCardProps> = ({ recipe, racks, isOpen, onClick }) => {
  const domain = sourceDomain(recipe.link);
  const pending = recipe.version === 0;
  const rackById = (id: string) => racks.find((r) => r.rackId === id);
  const ingredientCount = recipe.ingredients?.length ?? 0;

  const className = [
    'rr-recipecard',
    pending ? 'is-pending' : '',
    isOpen ? 'card-selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={className}
      onClick={() => onClick?.(recipe.recipeId)}
      aria-pressed={isOpen ? true : undefined}
    >
      {recipe.imageUrl ? (
        <div
          className="rr-recipecard-thumb"
          style={{ backgroundImage: `url(${recipe.imageUrl})` }}
          aria-hidden="true"
        />
      ) : null}
      <div className="rr-recipecard-meta">
        <span>{pending ? domain || 'scraping…' : domain || 'recipe'}</span>
        {!pending && ingredientCount > 0 && (
          <>
            <span className="rr-recipecard-dot" aria-hidden="true" />
            <span>
              {ingredientCount} ingredient{ingredientCount === 1 ? '' : 's'}
            </span>
          </>
        )}
      </div>
      <h3 className="rr-recipecard-title">
        {pending ? 'Cooking up your recipe…' : recipe.title || 'Untitled recipe'}
      </h3>
      {pending ? (
        <div className="rr-recipecard-progress" aria-hidden="true" />
      ) : (
        <div className="rr-recipecard-pills">
          {(recipe.rackIds ?? []).slice(0, 2).map((id) => {
            const rk = rackById(id);
            if (!rk) return null;
            return (
              <RackChip key={id} static>
                {rk.name}
              </RackChip>
            );
          })}
        </div>
      )}
    </button>
  );
};

export default RecipeCard;
